import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { getGetChatQueryKey, useGetChat, useGetTerms } from "~/api/chat/chat";
import { useGetPageTextContent } from "~/api/documents/documents";
import { useGetKnowledgebasePageContent } from "~/api/knowledge/knowledge";
import { Chat, ChatMessage, SourceReference } from "~/api/model";
import { useGetCurrentUser } from "~/api/users/users";
import skyLogoIcon from "~/assets/LogoSymbol.png";
import { ContentCopyButton } from "~/components/ContentCopyButton";
import { MarkdownContentModal } from "~/components/MarkdownContentModal";
import { MarkdownExportButton } from "~/components/MarkdownExportButton";
import { TabControlBar } from "~/components/TabControlBar";
import { TTSButton } from "~/components/TTSButton";
import { Button } from "~/components/ui/button";
import {
  ChatBubble,
  ChatBubbleMessage,
} from "~/components/ui/chat/chat-bubble";
import { ChatInput } from "~/components/ui/chat/chat-input";
import { ChatMessageList } from "~/components/ui/chat/chat-message-list";
import { UnifiedMarkdown } from "~/components/UnifiedMarkdown";
import { useSSEChatMessage } from "~/hooks/useSSEChatMessage";
import { trackEvent } from "~/utils/analytics";

import Loader from "~/assets/Loader.svg";
import { useParentCase } from "~/features/case/hooks/useParentCase";
import { useDocList } from "~/features/chat/hooks/useDocList";
import { useMergedTerms } from "~/features/chat/hooks/useMergedTerms";
import { useTermsStore } from "~/features/chat/stores/termsStore";
import {
  formatDate,
  generateChatMarkdownContent,
  ROUTES,
  useChatDocumentParams,
} from "~/helpers";
import { useMobile } from "~/hooks/use-mobile";
import { useDocumentSearchParams } from "~/hooks/useDocumentSearchParams";
import { useSourceMapDelegation } from "~/utils/markdownTerms";
import { rehypeTtsHighlight } from "~/utils/rehypeTtsHighlight";
import {
  convertSourcesToBatesText,
  formatCaseInfo,
  getFileTitle,
  injectBatesLabels,
  normalizeCitationMarkers,
  stripSourcesFromContent,
} from "../helpers";
import { Hint } from "../types";
import { AsyncReportBlock } from "./AsyncReportBlock";
import { ChatDisclaimerBanner } from "./ChatDisclaimerBanner";
import { ChatHistorySelector } from "./ChatHistorySelector";
import { ClearChatButton } from "./ClearChat";
import { PromptTips } from "./PromptTips";
import { SuggestQuestions } from "./SuggestQuestions";

export const ChatArea: FC = () => {
  const navigate = useNavigate();
  const { organizationId, caseId, chatId, documentId, isGuest } =
    useChatDocumentParams();
  const {
    data: chat,
    isLoading,
    error,
  } = useGetChat(organizationId, caseId, chatId);
  const { data: currentUser } = useGetCurrentUser();
  const { project } = useParentCase();
  const { docList, pageItems, absolutePageNumbers } = useDocList();
  const hasBatesNumbers = useMemo(
    () => pageItems.some((p) => p.batesNumber),
    [pageItems]
  );

  const [, updateDocumentParams] = useDocumentSearchParams();
  const { formatMessage } = useIntl();
  const { isMobile } = useMobile();

  useEffect(() => {
    if (
      error &&
      (error as unknown as AxiosError)?.response?.status === 403 &&
      currentUser?.guestInOrganizations?.includes(organizationId)
    ) {
      navigate(ROUTES.ACCESS_DENIED);
    }
  }, [error, currentUser, navigate, organizationId]);

  const caseInfo = project ? formatCaseInfo(project) : undefined;

  // For single-document chats, hide sources since they come from the entire case
  const isSingleDocumentChat = !!documentId;

  const [chatInputHeight, setChatInputHeight] = useState(0);
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const [apiTerms, setApiTerms] = useState<Array<Hint>>([]);
  const [storeTerms, setStoreTerms] = useState<Array<Hint>>([]);
  const [readingMap, setReadingMap] = useState<
    Record<string, { reading: boolean; idx: number | null; len: number }>
  >({});
  const setMsgState = (
    id: string,
    next: { reading: boolean; idx: number | null; len: number }
  ) => setReadingMap((m) => ({ ...m, [id]: next }));
  const getMsgState = (id: string) =>
    readingMap[id] ?? { reading: false, idx: null, len: 0 };

  const [pageTextModalOpen, setPageTextModalOpen] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [pageImageUrl, setPageImageUrl] = useState<string | undefined>(
    undefined
  );
  const [pageRotation, setPageRotation] = useState(0);

  // Knowledge base source state
  const [selectedKbDocumentId, setSelectedKbDocumentId] = useState<
    string | null
  >(null);
  const [selectedKbPageNumber, setSelectedKbPageNumber] = useState<
    number | null
  >(null);
  const [isKnowledgeBaseSource, setIsKnowledgeBaseSource] = useState(false);
  const [feedbackById, setFeedbackById] = useState<
    Record<string, "up" | "down" | null>
  >({});

  const previousScrollTop = useRef(0);
  const queryClient = useQueryClient();

  // Fetch case document page content
  const {
    data: pageContentData,
    isLoading: isPageContentLoading,
    error: pageContentError,
  } = useGetPageTextContent(
    organizationId,
    project?.id || "",
    selectedPageId ?? "",
    {
      query: {
        enabled:
          !isKnowledgeBaseSource &&
          !!selectedPageId &&
          !!project?.id &&
          selectedPageId !== "unknown" &&
          organizationId !== "unknown",
      },
    }
  );

  // Fetch knowledge base document page content
  const {
    data: kbPageContentData,
    isLoading: isKbPageContentLoading,
    error: kbPageContentError,
  } = useGetKnowledgebasePageContent(
    organizationId,
    selectedKbDocumentId ?? "",
    selectedKbPageNumber ?? 0,
    {
      query: {
        enabled:
          isKnowledgeBaseSource &&
          !!selectedKbDocumentId &&
          !!selectedKbPageNumber &&
          selectedKbPageNumber > 0 &&
          organizationId !== "unknown",
      },
    }
  );

  // SSE streaming chat functionality
  const handleSSEMessageEnd = useCallback(
    (event: {
      content: string;
      sources?: SourceReference[];
      messageId: string;
    }) => {
      const assistantMessage: ChatMessage = {
        id: event.messageId,
        role: "assistant",
        content: event.content,
        timestamp: Date.now(),
        sources: event.sources,
      };

      queryClient.setQueryData(
        getGetChatQueryKey(organizationId, caseId, chatId),
        (oldData: Chat | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            messages: [...(oldData.messages || []), assistantMessage],
          };
        }
      );
    },
    [queryClient, organizationId, caseId, chatId]
  );

  const handleAsyncReportScheduled = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getGetChatQueryKey(organizationId, caseId, chatId),
    });
  }, [queryClient, organizationId, caseId, chatId]);

  const {
    sendMessage: sendSSEMessage,
    cancelStream: _cancelStream,
    isStreaming,
    isThinking,
    isComprehensiveProcessing,
    comprehensiveStage,
    streamingContent,
    error: sseError,
  } = useSSEChatMessage({
    onMessageEnd: handleSSEMessageEnd,
    onAsyncReportScheduled: handleAsyncReportScheduled,
  });
  void _cancelStream;

  const handleFeedbackClick = (messageId: string, vote: "up" | "down") => {
    const previousFeedback = feedbackById[messageId];
    const newFeedback = previousFeedback === vote ? null : vote;

    setFeedbackById((prev) => ({
      ...prev,
      [messageId]: newFeedback,
    }));

    trackEvent({
      eventName: "chat_response_feedback",
      properties: {
        vote: newFeedback,
        chat_id: chatId,
        message_id: messageId,
      },
    });
  };

  const { data: termsData } = useGetTerms(organizationId, caseId, chatId, {
    query: {
      staleTime: Infinity,
    },
  });

  useEffect(() => {
    if (termsData?.terms) {
      setApiTerms(
        termsData.terms.filter(
          (term) => !["#", "term", "data"].includes(term.term.toLowerCase())
        )
      );
    }
  }, [termsData]);

  useEffect(() => {
    const currentTerms = useTermsStore.getState().getTerms(caseId);
    setStoreTerms(currentTerms);

    const unsubscribe = useTermsStore.subscribe((state) => {
      const newTerms = state.getTerms(caseId);
      setStoreTerms(newTerms);
    });

    return () => unsubscribe();
  }, [caseId]);

  const combinedAiTerms = useMemo(() => {
    return [...apiTerms, ...storeTerms];
  }, [apiTerms, storeTerms]);

  const allTerms = useMergedTerms(combinedAiTerms);

  const handleSendMessage = async (message: string) => {
    trackEvent({ eventName: "app/doc_list/chat_input.input/click" });
    const userMessage: ChatMessage = {
      id: String(Date.now()),
      role: "user",
      content: message,
      timestamp: Date.now(),
    };
    const updatedMessages = (chat?.messages || []).concat(userMessage);
    queryClient.setQueryData(
      getGetChatQueryKey(organizationId, caseId, chatId),
      {
        ...chat,
        messages: updatedMessages,
      }
    );

    sendSSEMessage(message);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.currentTarget;

    if (scrollTop < previousScrollTop.current) {
      setIsScrolledUp(true);
    } else {
      setIsScrolledUp(false);
    }

    previousScrollTop.current = scrollTop;
  };

  const isChatEmpty = isLoading || chat?.messages.length === 0;
  const isMessageLoading = isStreaming;
  const chatError = sseError;

  const [chatWrapperEl, setChatWrapperEl] = useState<HTMLDivElement | null>(
    null
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
  };

  useLayoutEffect(() => {
    scrollToBottom();
  }, [chat?.messages]);

  const handleHeightChange = (height: number) => {
    setChatInputHeight(height);
  };

  // Handle page reference clicks
  const handlePageReferenceClick = useCallback(
    async (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains("page-reference")) {
        event.preventDefault();

        const pageId = target.dataset.pageId;
        const pageOrgId = target.dataset.organizationId;
        const documentId = target.dataset.documentId;
        const isKbSource = target.dataset.isKnowledgeBase === "true";
        const batesNumber = target.dataset.batesNumber;

        trackEvent({
          eventName: "app/chat/source_citation.link/click",
          properties: {
            hasBatesNumber: !!batesNumber,
            sourceType: isKbSource ? "kb" : "document",
          },
        });

        // Handle standalone Bates citation (no pageId/organizationId)
        if (batesNumber && (!pageId || !pageOrgId)) {
          // Search for page by Bates number in loaded pages
          const normalize = (s: string) =>
            s.replace(/[\r\n]+/g, " ").toLowerCase();
          const pageItem = pageItems.find(
            (p) =>
              p.batesNumber &&
              normalize(p.batesNumber) === normalize(batesNumber)
          );

          if (pageItem) {
            setIsKnowledgeBaseSource(false);
            setSelectedKbDocumentId(null);
            setSelectedKbPageNumber(null);
            setSelectedPageId(pageItem.pageId);
            setIsContentLoading(true);
            setPageTextModalOpen(true);
            setPageRotation(pageItem.rotate);

            try {
              const urls = await pageItem.getUrls();
              setPageImageUrl(urls.retinaUrl || urls.highResUrl || undefined);
            } catch (error) {
              setPageImageUrl(undefined);
            } finally {
              setIsContentLoading(false);
            }
          }
          return;
        }

        // Check for unknown IDs
        if (
          pageId === "unknown" ||
          pageOrgId === "unknown" ||
          !pageId ||
          !pageOrgId
        ) {
          return;
        }

        if (isKbSource) {
          // Knowledge base source - pageId is the page number, documentId is the knowledgeDocumentId
          const pageNumber = parseInt(pageId, 10);
          if (!isNaN(pageNumber) && documentId) {
            setIsKnowledgeBaseSource(true);
            setSelectedKbDocumentId(documentId);
            setSelectedKbPageNumber(pageNumber);
            setSelectedPageId(null);
            setPageImageUrl(undefined);
            setPageRotation(0);
            setIsContentLoading(false);
            setPageTextModalOpen(true);
          }
        } else {
          // Case document source - pageId is the page UUID
          setIsKnowledgeBaseSource(false);
          setSelectedKbDocumentId(null);
          setSelectedKbPageNumber(null);
          setSelectedPageId(pageId);
          setIsContentLoading(true);
          setPageTextModalOpen(true);

          const pageItem = pageItems.find((p) => p.pageId === pageId);
          if (pageItem) {
            setPageRotation(pageItem.rotate);

            try {
              const urls = await pageItem.getUrls();
              setPageImageUrl(urls.retinaUrl || urls.highResUrl || undefined);
            } catch (error) {
              setPageImageUrl(undefined);
            } finally {
              setIsContentLoading(false);
            }
          } else {
            setIsContentLoading(false);
          }
        }
      }
    },
    [pageItems]
  );

  // Add event listener for page reference clicks
  useEffect(() => {
    document.addEventListener("click", handlePageReferenceClick);
    return () => {
      document.removeEventListener("click", handlePageReferenceClick);
    };
  }, [handlePageReferenceClick]);

  const { chatMarkdownContent, exportFileName } = useMemo(() => {
    // For single-document chats, strip sources from exported content
    const messages = isSingleDocumentChat
      ? (chat?.messages || []).map((msg) => ({
          ...msg,
          content:
            msg.role === "assistant"
              ? stripSourcesFromContent(msg.content)
              : msg.content,
        }))
      : chat?.messages || [];

    const chatMarkdownContent = generateChatMarkdownContent({
      title: chat?.title ?? "Unknown",
      messages,
      date: new Date().toISOString(),
    });

    const t = getFileTitle(chat?.title ?? "Unknown");
    const exportFileName = `${formatMessage({ id: `chat.export.filePrefix` })} "${t}" (${formatDate(new Date().toISOString())})`;

    return {
      chatMarkdownContent,
      exportFileName,
    };
  }, [chat?.title, chat?.messages, formatMessage, isSingleDocumentChat]);

  const handleSectionClick = useCallback(
    (sectionId: string, firstPageId: string) => {
      updateDocumentParams({
        sectionId,
        pageId: firstPageId,
        tab: "chat",
      });
    },
    [updateDocumentParams]
  );

  const handleBatesClick = useCallback(
    (pageId: string) => {
      updateDocumentParams({ pageId });
    },
    [updateDocumentParams]
  );

  useSourceMapDelegation(chatWrapperEl, handleSectionClick, handleBatesClick);

  const modalTitle = useMemo(() => {
    if (!selectedPageId) return "Page Content Recognition";

    const pageItem = pageItems.find((p) => p.pageId === selectedPageId);
    if (!pageItem) return "Page Content Recognition";

    const section = docList?.tree
      .flatMap((doc) => doc.sections)
      .find((sec) => sec.id === pageItem.sectionId);

    const sectionName = section?.name || "Unknown Section";
    const pageNumber = absolutePageNumbers[selectedPageId] || 0;

    return `${sectionName} – Page ${pageNumber}`;
  }, [selectedPageId, pageItems, docList, absolutePageNumbers]);

  useEffect(() => {
    if (!pageTextModalOpen) {
      setPageImageUrl(undefined);
      setPageRotation(0);
    }
  }, [pageTextModalOpen]);

  return (
    <div
      className={`bg-background flex flex-col ${isMobile ? "h-docsidebar-content-mobile" : "h-[calc(100vh-60px)]"}`}
    >
      {!isGuest && <ChatHistorySelector />}
      <ChatDisclaimerBanner />
      <div
        ref={setChatWrapperEl}
        className="chat-wrapper overflow-y-auto overflow-x-hidden flex-1 min-h-0"
        onScroll={handleScroll}
      >
        <ChatMessageList className="min-h-full relative flex flex-col">
          {isChatEmpty && (
            <div className="p-4 flex-grow flex flex-col justify-center items-center gap-3">
              <div className="bg-background size-[64px] rounded-full flex justify-center items-center relative">
                <img src={skyLogoIcon} alt="Chat" className="h-[42px]" />
              </div>
              <span className="text-foreground text-sm font-normal">
                <FormattedMessage id="chat.welcomeMessage" />
              </span>
            </div>
          )}
          <SuggestQuestions
            onSuggestionClick={({ value }) => handleSendMessage(value)}
          />
          {isChatEmpty && (
            <PromptTips onTipClick={(doText) => handleSendMessage(doText)} />
          )}
          {chat?.messages.map((message) => {
            if (message.type === "async_report" && message.jobId) {
              return (
                <ChatBubble key={message.id} variant="received">
                  <ChatBubbleMessage variant="received">
                    <AsyncReportBlock jobId={message.jobId} />
                  </ChatBubbleMessage>
                </ChatBubble>
              );
            }
            return (
              <ChatBubble
                key={message.id}
                variant={message.role === "user" ? "sent" : "received"}
              >
                <div className="flex flex-col gap-1.5">
                  <ChatBubbleMessage
                    variant={message.role === "user" ? "sent" : "received"}
                  >
                    <UnifiedMarkdown
                      className="leading-5"
                      extraRehypePlugins={
                        message.role === "assistant" &&
                        getMsgState(message.id).reading &&
                        getMsgState(message.id).idx !== null &&
                        getMsgState(message.id).len > 0
                          ? [
                              rehypeTtsHighlight(
                                getMsgState(message.id).idx,
                                getMsgState(message.id).len
                              ),
                            ]
                          : []
                      }
                    >
                      {message.role === "assistant" &&
                      isSingleDocumentChat &&
                      getMsgState(message.id).reading
                        ? stripSourcesFromContent(message.content)
                        : injectBatesLabels(message.content)}
                    </UnifiedMarkdown>
                  </ChatBubbleMessage>
                  {message.role === "assistant" && (
                    <div className="h-4 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200">
                      <div className="inline-flex items-center gap-2">
                        <ContentCopyButton
                          content={
                            isSingleDocumentChat
                              ? stripSourcesFromContent(message.content)
                              : message.content
                          }
                          disabled={false}
                          color="muted-foreground"
                          preCopyMessageId="copy"
                          eventName="app/doc_list/copy_message.button/click"
                        />
                        <TTSButton
                          text={
                            isSingleDocumentChat
                              ? stripSourcesFromContent(message.content)
                              : message.content
                          }
                          isLoading={false}
                          color="muted-foreground"
                          onStart={() =>
                            setMsgState(message.id, {
                              reading: true,
                              idx: 0,
                              len: 0,
                            })
                          }
                          onEnd={() =>
                            setMsgState(message.id, {
                              reading: false,
                              idx: null,
                              len: 0,
                            })
                          }
                          onBoundary={({ charIndex, charLength }) =>
                            setMsgState(message.id, {
                              reading: true,
                              idx: charIndex,
                              len: charLength,
                            })
                          }
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 p-0"
                          onClick={() => handleFeedbackClick(message.id, "up")}
                          disabled={isMessageLoading}
                        >
                          <ThumbsUp
                            className={`h-3 w-3 ${
                              feedbackById[message.id] === "up"
                                ? "fill-blue-500 text-blue-500"
                                : "text-muted-foreground"
                            }`}
                          />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 p-0"
                          onClick={() =>
                            handleFeedbackClick(message.id, "down")
                          }
                          disabled={isMessageLoading}
                        >
                          <ThumbsDown
                            className={`h-3 w-3 ${
                              feedbackById[message.id] === "down"
                                ? "fill-red-500 text-red-500"
                                : "text-muted-foreground"
                            }`}
                          />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </ChatBubble>
            );
          })}

          {isStreaming && streamingContent && (
            <ChatBubble variant="received">
              <ChatBubbleMessage variant="received">
                <UnifiedMarkdown className="leading-5">
                  {normalizeCitationMarkers(streamingContent)}
                </UnifiedMarkdown>
              </ChatBubbleMessage>
            </ChatBubble>
          )}
          {isComprehensiveProcessing && !streamingContent && (
            <ChatBubble variant="received">
              <ChatBubbleMessage variant="received">
                <div className="flex items-center gap-2">
                  <img
                    src={Loader}
                    alt="Loader"
                    height={16}
                    width={16}
                    className="dark:invert"
                  />
                  <span className="text-sm text-muted-foreground">
                    {comprehensiveStage || (
                      <FormattedMessage id="chat.comprehensiveProgress" />
                    )}
                  </span>
                </div>
              </ChatBubbleMessage>
            </ChatBubble>
          )}
          {isThinking && !isComprehensiveProcessing && !streamingContent && (
            <ChatBubble variant="received">
              <ChatBubbleMessage variant="received">
                <div className="flex items-center gap-2">
                  <img
                    src={Loader}
                    alt="Loader"
                    height={16}
                    width={16}
                    className="dark:invert"
                  />
                  <span className="text-sm text-muted-foreground">
                    <FormattedMessage id="chat.thinking" />
                  </span>
                </div>
              </ChatBubbleMessage>
            </ChatBubble>
          )}
          {(isLoading ||
            (isMessageLoading &&
              !streamingContent &&
              !isThinking &&
              !isComprehensiveProcessing)) && (
            <ChatBubble variant="received">
              <img
                src={Loader}
                alt="Loader"
                height={20}
                width={20}
                className="dark:invert"
              />
            </ChatBubble>
          )}
          {chatError && (
            <span className="text-xs text-red-500">
              <FormattedMessage
                id="chat.error"
                defaultMessage="Something went wrong. Please try again later."
              />
            </span>
          )}
        </ChatMessageList>
        <div ref={messagesEndRef} />
        {isScrolledUp && (
          <div
            className="h-[12px] bg-gradient-to-b from-transparent to-background fixed bottom-0 w-full"
            style={{
              bottom: `${chatInputHeight + 12 + 32 + 24}px`,
            }}
          />
        )}
      </div>
      <p className="text-center text-[10px] text-muted-foreground py-1 whitespace-nowrap">
        <FormattedMessage id="aiDisclaimer" />
      </p>
      <div className="px-[12px] pb-[12px]">
        <ChatInput
          placeholder={formatMessage({ id: "chat.placeholder" })}
          onSend={handleSendMessage}
          disabled={isMessageLoading}
          onHeightChange={handleHeightChange}
          className="border-t"
        />
      </div>
      <TabControlBar className="inline-flex">
        <TabControlBar.Group className="gap-2 justify-center">
          <ClearChatButton disabled={isChatEmpty} />
          <ContentCopyButton
            content={chatMarkdownContent}
            disabled={isChatEmpty}
            eventName="app/doc_list/copy_all.button/click"
            preCopyMessageId="copy"
          />
          <MarkdownExportButton
            organizationId={organizationId}
            content={chatMarkdownContent}
            disabled={isChatEmpty}
            fileName={exportFileName}
            buttonSize="mini"
            openEventName="app/doc_list/export.button/click"
            exportEventName="app/doc_list/export_menu_open/export_action.link/click"
            title={formatMessage({
              id: "common.chat",
            })}
            transformContent={
              hasBatesNumbers
                ? convertSourcesToBatesText
                : stripSourcesFromContent
            }
            caseInfo={caseInfo}
          />
        </TabControlBar.Group>
      </TabControlBar>

      {pageTextModalOpen && (
        <MarkdownContentModal
          title={
            isKnowledgeBaseSource
              ? `${kbPageContentData?.filename ?? "Knowledge Base Document"} - Page ${selectedKbPageNumber}`
              : modalTitle
          }
          content={
            isKnowledgeBaseSource
              ? isKbPageContentLoading
                ? "Loading content..."
                : kbPageContentError
                  ? "Error loading content. This page may not have processed content available yet."
                  : kbPageContentData?.content ||
                    "No content available for this page."
              : isPageContentLoading || isContentLoading
                ? "Loading content..."
                : pageContentError
                  ? "Error loading content. This page may not have processed content available yet."
                  : pageContentData?.content ||
                    "No content available for this page."
          }
          filename={
            isKnowledgeBaseSource
              ? kbPageContentData?.filename
              : pageContentData?.filename
          }
          authorName={
            isKnowledgeBaseSource
              ? (kbPageContentData?.authorName ?? undefined)
              : pageContentData?.authorName
          }
          uploadDate={
            isKnowledgeBaseSource
              ? kbPageContentData?.uploadDate
              : pageContentData?.uploadDate
          }
          open={pageTextModalOpen}
          onOpenChange={(open) => {
            setPageTextModalOpen(open);
            if (!open) {
              // Reset KB source state when modal closes
              setIsKnowledgeBaseSource(false);
              setSelectedKbDocumentId(null);
              setSelectedKbPageNumber(null);
            }
          }}
          copyEventName="app/chat/page_text_modal/copy_all.button/click"
          exportOpenEventName="app/chat/page_text_modal/export.button/click"
          exportEventName="app/chat/page_text_modal/export_menu_open/export_action.link/click"
          terms={allTerms}
          pageImageUrl={
            isKnowledgeBaseSource
              ? (kbPageContentData?.scanUrl ?? undefined)
              : pageImageUrl
          }
          pageRotation={isKnowledgeBaseSource ? 0 : pageRotation}
        />
      )}
    </div>
  );
};
